import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import emailjs from '@emailjs/browser';
import logo from '../Assests/bookinindia-logo-optimized.png';

// Initialize EmailJS
emailjs.init('OVWrkvxi-7s6-GqNa');

const BookingSuccess = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { bookingId, customer, trip, total } = location.state || {};
  const [isSendingEmail, setIsSendingEmail] = useState(false);
  const [emailSent, setEmailSent] = useState(false);
  const [emailError, setEmailError] = useState(false);

  // Debug: Log the received data
  console.log('BookingSuccess - Received data:', { bookingId, customer, trip, total });
  console.log('BookingSuccess - Customer email:', customer?.email);

  // Function to compress image
  const compressImage = (imageData, maxWidth = 600) => {
    return new Promise((resolve) => {
      const img = new Image();
      img.onload = () => {
        const canvas = document.createElement('canvas');
        let width = img.width;
        let height = img.height;
        
        if (width > maxWidth) {
          height = (height * maxWidth) / width;
          width = maxWidth;
        }
        
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0, width, height);
        resolve(canvas.toDataURL('image/jpeg', 0.7));
      };
      img.src = imageData;
    });
  };

  // Function to send email with PDF as attachment
  const sendEmailWithPDFAttachment = async (pdfBase64) => {
    try {
      // Check if email exists
      if (!customer?.email) {
        throw new Error('Customer email is missing');
      }

      const logoUrl = 'https://www.bookinindia.com/logo.png';
      
      // Remove the data:application/pdf;base64, prefix
      const pdfData = pdfBase64.split(',')[1];
      
      // Use the exact parameter names from your EmailJS template
      const templateParams = {
        // Recipient email - matches your template's {{email}}
        email: customer.email,
        
        // Other variables that match your template
        to_name: customer?.fullName || 'Valued Customer',
        booking_id: bookingId || 'N/A',
        from_location: trip?.from || 'N/A',
        to_location: trip?.to || 'N/A',
        pickup_date: trip?.pickupDate || 'N/A',
        pickup_time: trip?.pickupTime || 'N/A',
        total_amount: total || '0',
        trip_type: trip?.tripType === 'roundtrip' ? 'Round Trip' : 'One Way',
        vehicle_name: trip?.selectedVehicle?.name || 'Sedan',
        logo_url: logoUrl,
        base_fare: trip?.priceBreakdown?.tripFare?.toLocaleString() || '0',
        driver_allowance: trip?.priceBreakdown?.driverAllowance?.toLocaleString() || '0',
        tax_amount: trip?.priceBreakdown?.estimatedTax?.toLocaleString() || '0',
        pdf_attachment: pdfData,
      };

      console.log('Sending email to:', customer.email);
      console.log('Template params:', templateParams);

      const response = await emailjs.send(
        'service_drsjkvo',
        'template_cmegx5g',
        templateParams,
        'OVWrkvxi-7s6-GqNa'
      );

      if (response.status === 200) {
        console.log('Email sent successfully with PDF attachment!');
        setEmailSent(true);
        setEmailError(false);
        setTimeout(() => setEmailSent(false), 5000);
        return true;
      } else {
        throw new Error('Failed to send email');
      }
    } catch (error) {
      console.error('Error sending email:', error);
      throw error;
    }
  };

  // Generate PDF and send email automatically
  useEffect(() => {
    const generateAndSendEmail = async () => {
      const receiptElement = document.getElementById('receipt-content');
      
      if (!receiptElement) {
        console.log('Receipt element not found');
        return;
      }
      
      if (!customer?.email) {
        console.log('Customer email is missing:', customer);
        setEmailError(true);
        return;
      }
      
      if (!bookingId) {
        console.log('Booking ID is missing');
        setEmailError(true);
        return;
      }

      if (emailSent || isSendingEmail) {
        console.log('Email already sent or in progress');
        return;
      }

      setIsSendingEmail(true);
      
      try {
        await new Promise(resolve => setTimeout(resolve, 500));
        
        const canvas = await html2canvas(receiptElement, {
          scale: 1.2,
          backgroundColor: '#ffffff',
          logging: false,
          useCORS: true,
        });
        
        const compressedImage = await compressImage(canvas.toDataURL('image/jpeg'), 500);
        
        const pdf = new jsPDF({
          orientation: 'portrait',
          unit: 'mm',
          format: 'a4',
          compress: true
        });
        
        const pdfWidth = pdf.internal.pageSize.getWidth();
        const pdfHeight = pdf.internal.pageSize.getHeight();
        
        pdf.addImage(compressedImage, 'JPEG', 0, 0, pdfWidth, pdfHeight, undefined, 'FAST');
        
        const pdfBase64 = pdf.output('datauristring');
        
        const pdfSizeInKB = Math.ceil((pdfBase64.length * 3) / 4 / 1024);
        console.log(`PDF size: ${pdfSizeInKB} KB`);
        
        await sendEmailWithPDFAttachment(pdfBase64);
        
      } catch (error) {
        console.error('Error:', error);
        setEmailError(true);
      } finally {
        setIsSendingEmail(false);
      }
    };

    if (customer?.email && bookingId && !emailSent && !isSendingEmail) {
      generateAndSendEmail();
    }
  }, []);

  const handleDownloadPDF = async () => {
    const receiptElement = document.getElementById('receipt-content');
    if (!receiptElement) return;

    try {
      const loadingToast = document.createElement('div');
      loadingToast.className = 'fixed top-4 right-4 bg-blue-500 text-white px-4 py-2 rounded-lg shadow-lg z-50';
      loadingToast.innerText = 'Generating PDF...';
      document.body.appendChild(loadingToast);

      const canvas = await html2canvas(receiptElement, {
        scale: 2,
        backgroundColor: '#ffffff',
        logging: false,
        useCORS: true,
      });
      
      const imgData = canvas.toDataURL('image/png');
      const pdf = new jsPDF({
        orientation: 'portrait',
        unit: 'mm',
        format: 'a4'
      });
      
      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = pdf.internal.pageSize.getHeight();
      let imgWidth = pdfWidth - 20;
      let imgHeight = (canvas.height * imgWidth) / canvas.width;
      
      if (imgHeight > pdfHeight - 20) {
        imgHeight = pdfHeight - 20;
        imgWidth = (canvas.width * imgHeight) / canvas.height;
      }
      
      const xPosition = (pdfWidth - imgWidth) / 2;
      const yPosition = (pdfHeight - imgHeight) / 2;
      
      pdf.addImage(imgData, 'PNG', xPosition, yPosition, imgWidth, imgHeight);
      pdf.save(`BookinIndia_Receipt_${bookingId}.pdf`);
      
      loadingToast.remove();
      
      const successToast = document.createElement('div');
      successToast.className = 'fixed top-4 right-4 bg-green-500 text-white px-4 py-2 rounded-lg shadow-lg z-50';
      successToast.innerText = 'PDF downloaded successfully!';
      document.body.appendChild(successToast);
      setTimeout(() => successToast.remove(), 3000);
      
    } catch (error) {
      console.error('Error generating PDF:', error);
      alert('Error generating PDF. Please try again.');
    }
  };

  const handleResendEmail = async () => {
    const receiptElement = document.getElementById('receipt-content');
    if (!receiptElement) return;

    setIsSendingEmail(true);
    setEmailError(false);
    
    try {
      const canvas = await html2canvas(receiptElement, {
        scale: 1,
        backgroundColor: '#ffffff',
        logging: false,
        useCORS: true,
      });
      
      const compressedImage = await compressImage(canvas.toDataURL('image/jpeg'), 450);
      const pdf = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4', compress: true });
      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = pdf.internal.pageSize.getHeight();
      pdf.addImage(compressedImage, 'JPEG', 0, 0, pdfWidth, pdfHeight, undefined, 'FAST');
      const pdfBase64 = pdf.output('datauristring');
      
      await sendEmailWithPDFAttachment(pdfBase64);
      
    } catch (error) {
      console.error('Error resending email:', error);
      setEmailError(true);
    } finally {
      setIsSendingEmail(false);
    }
  };

  const handleShare = async () => {
    const shareData = {
      title: 'Booking Confirmed - BookinIndia',
      text: `Booking ID: ${bookingId}\nCustomer: ${customer?.fullName}\nTrip: ${trip?.from} → ${trip?.to}\nDate: ${trip?.pickupDate}\nPayment: Pay to driver at pickup\n\nThank you for choosing BookinIndia!`,
      url: window.location.href,
    };

    if (navigator.share && navigator.canShare(shareData)) {
      try {
        await navigator.share(shareData);
      } catch (err) {
        console.log('Error sharing:', err);
      }
    } else {
      navigator.clipboard.writeText(`${shareData.title}\n\n${shareData.text}\n\nBookinIndia - Safe & Reliable Rides`);
      alert('Booking details copied to clipboard!');
    }
  };

  // Keep all your existing UI JSX here (the return statement with all the UI)
  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700&family=Syne:wght@500;600;700;800&display=swap');
        
        * {
          font-family: 'DM Sans', sans-serif;
        }
        
        .font-syne {
          font-family: 'Syne', sans-serif;
        }
        
        @keyframes slideUp {
          from {
            opacity: 0;
            transform: translateY(30px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        
        @keyframes scaleIn {
          from {
            transform: scale(0);
          }
          to {
            transform: scale(1);
          }
        }
        
        .animate-slideUp {
          animation: slideUp 0.5s ease-out;
        }
        
        .animate-scaleIn {
          animation: scaleIn 0.5s ease-out 0.2s both;
        }
        
        @keyframes bounce {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(-5px); }
        }
        
        .animate-bounce {
          animation: bounce 0.5s ease-out;
        }
        
        @keyframes pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.5; }
        }
        
        .animate-pulse {
          animation: pulse 1.5s ease-in-out infinite;
        }
      `}</style>

      {/* Hidden receipt content for PDF generation */}
      <div id="receipt-content" style={{ 
        position: 'absolute', 
        top: '-9999px', 
        left: '-9999px', 
        width: '650px', 
        background: 'white', 
        padding: '20px',
        fontFamily: 'DM Sans, sans-serif',
        fontSize: '12px'
      }}>
        {/* Header */}
        <div style={{ 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'space-between',
          marginBottom: '15px', 
          borderBottom: '2px solid #e71204', 
          paddingBottom: '10px'
        }}>
          <div style={{ width: '80px', textAlign: 'left' }}>
            <img src={logo} alt="BookinIndia" style={{ height: '100px', width: 'auto' }} crossOrigin="anonymous" />
          </div>
          <div style={{ textAlign: 'center', flex: 1 }}>
            <h1 style={{ color: '#e71204', fontSize: '20px', margin: '0', fontFamily: 'Syne, sans-serif' }}>
              Booking Confirmation
            </h1>
            <p style={{ color: '#666', fontSize: '9px', margin: '2px 0 0 0' }}>
              Your trusted travel partner
            </p>
          </div>
          <div style={{ width: '80px' }}></div>
        </div>
        
        {/* Booking ID */}
        <div style={{ background: 'linear-gradient(135deg, #fef3c7 0%, #fffbeb 100%)', padding: '10px', borderRadius: '8px', textAlign: 'center', marginBottom: '15px', border: '1px solid #fde68a' }}>
          <p style={{ fontSize: '9px', color: '#92400e', textTransform: 'uppercase', letterSpacing: '1px', margin: '0 0 3px 0' }}>Booking ID</p>
          <p style={{ fontSize: '20px', fontWeight: 'bold', color: '#e71204', fontFamily: 'monospace', margin: '0' }}>{bookingId || 'N/A'}</p>
        </div>
        
        {/* Two Column Layout */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px', marginBottom: '15px' }}>
          {/* Left Column */}
          <div>
            <div style={{ marginBottom: '12px' }}>
              <h3 style={{ fontSize: '12px', fontWeight: 'bold', color: '#e71204', marginBottom: '8px', borderLeft: '3px solid #e71204', paddingLeft: '8px' }}>
                👤 CUSTOMER DETAILS
              </h3>
              <div style={{ paddingLeft: '8px' }}>
                <p style={{ marginBottom: '4px', fontSize: '10px' }}><strong>Full Name:</strong> {customer?.fullName || 'N/A'}</p>
                <p style={{ marginBottom: '4px', fontSize: '10px' }}><strong>Mobile Number:</strong> {customer?.mobile || 'N/A'}</p>
                <p style={{ marginBottom: '0', fontSize: '10px' }}><strong>Email Address:</strong> {customer?.email || 'N/A'}</p>
              </div>
            </div>
            
            <div>
              <h3 style={{ fontSize: '12px', fontWeight: 'bold', color: '#e71204', marginBottom: '8px', borderLeft: '3px solid #e71204', paddingLeft: '8px' }}>
                🚗 VEHICLE DETAILS
              </h3>
              <div style={{ paddingLeft: '8px' }}>
                <p style={{ marginBottom: '4px', fontSize: '10px' }}><strong>Vehicle:</strong> {trip?.selectedVehicle?.name || 'N/A'}</p>
                <p style={{ marginBottom: '4px', fontSize: '10px' }}><strong>Type:</strong> {trip?.selectedVehicle?.type || 'N/A'}</p>
                <p style={{ marginBottom: '0', fontSize: '10px' }}><strong>Capacity:</strong> {trip?.selectedVehicle?.seaters || '4'} Seater</p>
              </div>
            </div>
          </div>
          
          {/* Right Column */}
          <div>
            <div style={{ marginBottom: '12px' }}>
              <h3 style={{ fontSize: '12px', fontWeight: 'bold', color: '#e71204', marginBottom: '8px', borderLeft: '3px solid #e71204', paddingLeft: '8px' }}>
                🗺️ TRIP DETAILS
              </h3>
              <div style={{ paddingLeft: '8px' }}>
                <p style={{ marginBottom: '4px', fontSize: '10px' }}><strong>From:</strong> {trip?.from || 'N/A'}</p>
                <p style={{ marginBottom: '4px', fontSize: '10px' }}><strong>To:</strong> {trip?.to || 'N/A'}</p>
                <p style={{ marginBottom: '4px', fontSize: '10px' }}><strong>Pickup Date:</strong> {trip?.pickupDate || 'N/A'}</p>
                <p style={{ marginBottom: '4px', fontSize: '10px' }}><strong>Pickup Time:</strong> {trip?.pickupTime || 'N/A'}</p>
                <p style={{ marginBottom: '0', fontSize: '10px' }}><strong>Trip Type:</strong> {trip?.tripType === 'roundtrip' ? 'Round Trip' : 'One Way'}</p>
              </div>
            </div>
          </div>
        </div>
        
        {/* Payment Instructions */}
        <div style={{ background: '#ecfdf5', padding: '8px', borderRadius: '6px', marginBottom: '10px', borderLeft: '3px solid #10b981' }}>
          <p style={{ color: '#065f46', fontSize: '9px', margin: '0' }}>
            <strong>💡 PAYMENT INSTRUCTIONS:</strong> Payment to be made directly to the driver at the time of pickup. No online payment required.
          </p>
        </div>
        
        {/* Important Notes */}
        <div style={{ marginBottom: '10px', padding: '8px', background: '#f9fafb', borderRadius: '6px' }}>
          <p style={{ fontSize: '9px', fontWeight: 'bold', marginBottom: '5px', color: '#374151' }}>📋 IMPORTANT NOTES:</p>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '4px', fontSize: '8px', color: '#6b7280' }}>
            <span>✓ Free cancellation up to 1 hour before departure</span>
            <span>✓ 24/7 customer support available</span>
            <span>✓ Please arrive 10 minutes before pickup time</span>
            <span>✓ Keep this receipt for your records</span>
            <span>✓ Driver details will be shared 2 hours before pickup</span>
            <span>✓ For assistance, call our support team</span>
          </div>
        </div>
        
        {/* Footer */}
        <div style={{ textAlign: 'center', paddingTop: '8px', borderTop: '1px solid #e5e7eb' }}>
          <p style={{ fontSize: '8px', color: '#9ca3af', margin: '0' }}>
            BookinIndia - Safe, Reliable & Comfortable Rides
          </p>
          <p style={{ fontSize: '7px', color: '#9ca3af', margin: '2px 0 0 0' }}>
            support@bookinindia.com | +91-6309376010 | www.bookinindia.com
          </p>
        </div>
      </div>

      {/* Desktop View */}
      <div className="hidden md:block min-h-screen bg-gradient-to-br from-orange-50 via-white to-orange-50">
        <div className="max-w-4xl mx-auto px-4 py-12">
          <div className="bg-white rounded-2xl shadow-xl overflow-hidden animate-slideUp">
            {/* Header */}
            <div className="bg-gradient-to-r from-green-500 to-green-600 px-8 py-12 text-center">
              <div className="w-20 h-20 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-6 animate-scaleIn">
                <svg className="w-10 h-10 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <h1 className="text-4xl font-bold text-white mb-2 font-syne">Booking Confirmed!</h1>
              <p className="text-orange-100">Your cab has been successfully booked</p>
            </div>

            {/* Content */}
            <div className="p-8">
              {/* Email Status Banner */}
              {/* <div className={`mb-6 rounded-xl p-4 ${emailSent ? 'bg-green-50 border border-green-200' : emailError ? 'bg-red-50 border border-red-200' : isSendingEmail ? 'bg-blue-50 border border-blue-200' : 'bg-gray-50 border border-gray-200'}`}>
                <div className="flex items-center gap-3">
                  {isSendingEmail ? (
                    <>
                      <div className="animate-spin rounded-full h-5 w-5 border-2 border-blue-500 border-t-transparent"></div>
                      <span className="text-sm text-blue-700">Sending confirmation email to {customer?.email}...</span>
                    </>
                  ) : emailSent ? (
                    <>
                      <svg className="w-5 h-5 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                      </svg>
                      <span className="text-sm text-green-700">✓ Confirmation email sent to {customer?.email}</span>
                    </>
                  ) : emailError ? (
                    <>
                      <svg className="w-5 h-5 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                      </svg>
                      <span className="text-sm text-red-700">Failed to send email. <button onClick={handleResendEmail} className="underline font-medium">Click here to resend</button></span>
                    </>
                  ) : null}
                </div>
              </div> */}

              {/* Booking ID */}
              <div className="bg-amber-50 border border-amber-200 rounded-xl p-6 text-center mb-8">
                <p className="text-xs font-semibold text-amber-800 uppercase tracking-wider">Booking ID</p>
                <p className="text-3xl font-bold text-orange-600 font-mono mt-2">{bookingId || 'N/A'}</p>
              </div>

              {/* Customer Details */}
              <div className="mb-8">
                <h3 className="text-lg font-bold text-gray-800 mb-4 flex items-center gap-2">
                  <span className="text-2xl">👤</span> Customer Details
                </h3>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-xs text-gray-500 uppercase font-semibold">Full Name</p>
                    <p className="text-gray-800 font-medium mt-1">{customer?.fullName || 'N/A'}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500 uppercase font-semibold">Mobile</p>
                    <p className="text-gray-800 font-medium mt-1">{customer?.mobile || 'N/A'}</p>
                  </div>
                  <div className="col-span-2">
                    <p className="text-xs text-gray-500 uppercase font-semibold">Email</p>
                    <p className="text-gray-800 font-medium mt-1">{customer?.email || 'N/A'}</p>
                  </div>
                </div>
              </div>

              {/* Trip Details */}
              <div className="mb-8">
                <h3 className="text-lg font-bold text-gray-800 mb-4 flex items-center gap-2">
                  <span className="text-2xl">🗺️</span> Trip Details
                </h3>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-xs text-gray-500 uppercase font-semibold">From</p>
                    <p className="text-gray-800 font-medium mt-1">{trip?.from || 'N/A'}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500 uppercase font-semibold">To</p>
                    <p className="text-gray-800 font-medium mt-1">{trip?.to || 'N/A'}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500 uppercase font-semibold">Pickup Date</p>
                    <p className="text-gray-800 font-medium mt-1">{trip?.pickupDate || 'N/A'}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500 uppercase font-semibold">Pickup Time</p>
                    <p className="text-gray-800 font-medium mt-1">{trip?.pickupTime || 'N/A'}</p>
                  </div>
                </div>
              </div>

              {/* Vehicle Details */}
              <div className="mb-8">
                <h3 className="text-lg font-bold text-gray-800 mb-4 flex items-center gap-2">
                  <span className="text-2xl">🚗</span> Vehicle Details
                </h3>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-xs text-gray-500 uppercase font-semibold">Vehicle</p>
                    <p className="text-gray-800 font-medium mt-1">{trip?.selectedVehicle?.name || 'N/A'}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500 uppercase font-semibold">Type</p>
                    <p className="text-gray-800 font-medium mt-1">{trip?.selectedVehicle?.type || 'N/A'}</p>
                  </div>
                </div>
              </div>

              {/* Payment Note */}
              <div className="bg-green-50 border-l-4 border-green-500 rounded-lg p-4 mb-8">
                <p className="text-green-800 text-sm">
                  💡 <strong>Payment Instructions:</strong> Please pay directly to the driver at the time of pickup. No online payment required.
                </p>
              </div>

              {/* Action Buttons */}
              <div className="flex gap-4">
                <button
                  onClick={() => navigate('/')}
                  className="flex-1 bg-orange-500 text-white py-3 rounded-xl font-semibold hover:bg-orange-600 transition shadow-md"
                >
                  Go to Home
                </button>
                <button
                  onClick={handleDownloadPDF}
                  className="flex-1 bg-white text-orange-600 border-2 border-orange-500 py-3 rounded-xl font-semibold hover:bg-orange-50 transition"
                >
                  Download PDF
                </button>
                <button
                  onClick={handleResendEmail}
                  disabled={isSendingEmail}
                  className="flex-1 bg-green-500 text-white py-3 rounded-xl font-semibold hover:bg-green-600 transition disabled:opacity-50"
                >
                  {isSendingEmail ? 'Sending...' : 'Resend Email'}
                </button>
                <button
                  onClick={handleShare}
                  className="flex-1 bg-white text-orange-600 border-2 border-orange-500 py-3 rounded-xl font-semibold hover:bg-orange-50 transition"
                >
                  Share
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Mobile View - App Style */}
      <div className="md:hidden min-h-screen bg-gray-100">
        {/* Header */}
        <div className="bg-gradient-to-r from-green-500 to-green-600 pt-12 pb-8 px-6 rounded-b-3xl -mt-20">
          <div className="text-center">
            <div className="w-20 h-20 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-4 animate-bounce">
              <svg className="w-10 h-10 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <h1 className="text-2xl font-bold text-white font-syne">Booking Confirmed!</h1>
            <p className="text-orange-100 text-sm mt-1">Your cab is ready for pickup</p>
          </div>
        </div>

        {/* Content */}
        <div className="px-4 -mt-4 pb-32">
          {/* Email Status Card */}
          {/* <div className={`rounded-2xl p-4 mb-4 ${emailSent ? 'bg-green-50 border border-green-200' : emailError ? 'bg-red-50 border border-red-200' : isSendingEmail ? 'bg-blue-50 border border-blue-200' : 'bg-gray-50 border border-gray-200'}`}>
            <div className="flex items-center gap-2">
              {isSendingEmail ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-2 border-blue-500 border-t-transparent"></div>
                  <span className="text-xs text-blue-700">Sending email...</span>
                </>
              ) : emailSent ? (
                <>
                  <svg className="w-4 h-4 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                  </svg>
                  <span className="text-xs text-green-700">✓ Email sent to {customer?.email?.split('@')[0]}***</span>
                </>
              ) : emailError ? (
                <>
                  <svg className="w-4 h-4 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                  <span className="text-xs text-red-700">Failed to send email</span>
                  <button onClick={handleResendEmail} className="text-xs text-red-700 underline ml-auto">Resend</button>
                </>
              ) : null}
            </div>
          </div> */}

          {/* Booking ID Card */}
          <div className="bg-white rounded-2xl p-5 shadow-lg mb-4 animate-slideUp">
            <p className="text-xs font-semibold text-amber-600 uppercase tracking-wider text-center">Booking ID</p>
            <p className="text-xl font-bold text-orange-600 text-center font-mono mt-1">{bookingId || 'N/A'}</p>
          </div>

          {/* Customer Section */}
          <div className="bg-white rounded-2xl p-5 shadow-lg mb-4 animate-slideUp">
            <h3 className="font-bold text-gray-800 mb-3 flex items-center gap-2">
              <span className="text-xl">👤</span> Customer Details
            </h3>
            <div className="space-y-2">
              <div className="flex justify-between items-center py-1 border-b border-gray-100">
                <span className="text-xs text-gray-500">Full Name</span>
                <span className="text-sm font-medium text-gray-800">{customer?.fullName || 'N/A'}</span>
              </div>
              <div className="flex justify-between items-center py-1 border-b border-gray-100">
                <span className="text-xs text-gray-500">Mobile</span>
                <span className="text-sm font-medium text-gray-800">{customer?.mobile || 'N/A'}</span>
              </div>
              <div className="flex justify-between items-center py-1">
                <span className="text-xs text-gray-500">Email</span>
                <span className="text-sm font-medium text-gray-800 truncate">{customer?.email || 'N/A'}</span>
              </div>
            </div>
          </div>

          {/* Trip Section */}
          <div className="bg-white rounded-2xl p-5 shadow-lg mb-4 animate-slideUp">
            <h3 className="font-bold text-gray-800 mb-3 flex items-center gap-2">
              <span className="text-xl">🗺️</span> Trip Details
            </h3>
            <div className="space-y-2">
              <div className="flex justify-between items-center py-1 border-b border-gray-100">
                <span className="text-xs text-gray-500">Route</span>
                <span className="text-sm font-medium text-gray-800">{trip?.from} → {trip?.to}</span>
              </div>
              <div className="flex justify-between items-center py-1 border-b border-gray-100">
                <span className="text-xs text-gray-500">Pickup</span>
                <span className="text-sm font-medium text-gray-800">{trip?.pickupDate} at {trip?.pickupTime}</span>
              </div>
              <div className="flex justify-between items-center py-1">
                <span className="text-xs text-gray-500">Trip Type</span>
                <span className="text-sm font-medium text-gray-800">{trip?.tripType === 'roundtrip' ? 'Round Trip' : 'One Way'}</span>
              </div>
            </div>
          </div>

          {/* Vehicle Section */}
          <div className="bg-white rounded-2xl p-5 shadow-lg mb-4 animate-slideUp">
            <h3 className="font-bold text-gray-800 mb-3 flex items-center gap-2">
              <span className="text-xl">🚗</span> Vehicle Details
            </h3>
            <div className="flex justify-between items-center">
              <div>
                <p className="text-xs text-gray-500">Vehicle</p>
                <p className="font-semibold text-gray-800">{trip?.selectedVehicle?.name || 'N/A'}</p>
              </div>
              <div className="text-right">
                <p className="text-xs text-gray-500">Type</p>
                <p className="font-semibold text-gray-800">{trip?.selectedVehicle?.type || 'N/A'}</p>
              </div>
            </div>
          </div>

          {/* Payment Note */}
          <div className="bg-green-50 rounded-2xl p-4 shadow-lg mb-4">
            <p className="text-green-800 text-xs leading-relaxed">
              💡 <strong>Pay to Driver:</strong> Please pay directly to the driver at pickup. No online payment required.
            </p>
          </div>
        </div>

        {/* Bottom Sticky Bar */}
        <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 px-4 py-3 shadow-lg">
          <div className="flex gap-3">
            <button
              onClick={() => navigate('/')}
              className="flex-1 bg-orange-500 text-white py-3 rounded-xl font-semibold text-sm active:scale-95 transition"
            >
              Home
            </button>
            <button
              onClick={handleDownloadPDF}
              className="flex-1 bg-white text-orange-600 border-2 border-orange-500 py-3 rounded-xl font-semibold text-sm active:scale-95 transition"
            >
              PDF
            </button>
            {/* <button
              onClick={handleResendEmail}
              disabled={isSendingEmail}
              className="flex-1 bg-green-500 text-white py-3 rounded-xl font-semibold text-sm active:scale-95 transition disabled:opacity-50"
            >
              {isSendingEmail ? '...' : emailSent ? '✓ Sent' : 'Resend'}
            </button> */}
            <button
              onClick={handleShare}
              className="flex-1 bg-white text-orange-600 border-2 border-orange-500 py-3 rounded-xl font-semibold text-sm active:scale-95 transition"
            >
              Share
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

export default BookingSuccess;
